export const VEHICLE_TITLE_TRANSFER_TEMPLATE = `{{#if vehicle_state_has_guide}}
# What This Guide Covers

{{requestor_first_name}}, this guide walks you through transferring {{deceased_first_name}}'s vehicle into {{user_full_name}}'s name in {{vehicle_state}}.

We've broken it down into clear steps so you know exactly what to expect.

You don't need to do this all at once. Take it at your own pace.

# You'll Need Legal Authority First

Before you can transfer a vehicle title out of your loved one's name, the state needs to know you're authorized to act on behalf of the estate. This comes in the form of a legal document, and you may not have it yet. That's completely normal.

Here are the most common types of legal authority:
- **Letters Testamentary (LOT)** - issued by a probate court when there's a will
- **Letters of Administration** - issued by a probate court when there's no will
- **Small Estate Affidavit** - a simplified option for estates under a certain value
- **Trust Certificate** - if assets were held in a trust

Until you have one of these, the title office won't be able to process the transfer. But that doesn't mean you have to wait to get started - there are meaningful things you can do right now.

# What You Can Do Right Now

While you're waiting for your legal authority, here are some steps you can take to stay ahead. Every little bit helps.

- [ ] Keep the vehicle in a safe, secure location.
- [ ] Maintain insurance coverage. Most policies stay active for a period after a loss, but check with your provider to be sure.
- [ ] Gather the documents you'll need later: a certified copy of the death certificate, the vehicle's current title (check the glove box, filing cabinet, or safe), and your photo ID.
- [ ] Hold off on signing the back of the title for now. Wait until you have your legal documents in hand.

# Paths That Do Not Require Legal Authority

Depending on how the title is set up, there are ways to transfer the vehicle without going through probate or waiting for legal documents. Here's what applies in {{vehicle_state}}.

Not sure how the title is set up? Check the physical title document for terms like "Transfer on Death," "TOD," "Joint Tenancy," "Right of Survivorship," or "or" between owner names. If you can't find the title, contact us - we can help you figure out your options.

{{#if vehicle_state_CA}}
**If {{deceased_first_name}} registered a Transfer on Death (TOD) beneficiary on the title (Form REG 227),** the named beneficiary can transfer directly without probate.

**Where to go:** Visit a California DMV field office.

**What to do:**
- Confirm the TOD beneficiary designation is on the title (Form REG 227 should already be on file with the DMV).
- Complete Form REG 343 (Application for Title or Registration).
- Bring everything listed below to a DMV field office.
- Sign as the beneficiary - not as an executor or representative.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| REG 343 | Application for Title or Registration |

[Download REG 343](https://www.dmv.ca.gov/portal/file/application-for-title-or-registration-reg-343-pdf/)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID (California driver license or ID card)
- Completed Form REG 343

**Fees:** Approximately $15 for the title transfer fee, plus registration fees. Confirm with your local office before you go.

Make an appointment online at dmv.ca.gov to avoid long wait times.

---

**If the title lists two owners with "or" between their names,** the surviving owner can transfer with just a death certificate.

**Where to go:** Visit a California DMV field office.

**What to do:**
- Check the title - look for "or" between the two owner names (not "and").
- Complete Form REG 343.
- Bring everything listed below to a DMV field office.
- Sign as the surviving owner.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| REG 343 | Application for Title or Registration |

[Download REG 343](https://www.dmv.ca.gov/portal/file/application-for-title-or-registration-reg-343-pdf/)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing joint ownership with "or")
- Certified copy of the death certificate
- Your photo ID

**Fees:** Approximately $15 for the title transfer fee, plus registration fees. Confirm with your local office before you go.

---

If the estate is small enough (the threshold is adjusted each year - the DMV can confirm the current amount), you can use Form REG 5 to transfer without going through probate. Vehicles don't count toward this total. Also, if the title lists two owners with "or" between their names, the surviving owner can transfer with just a death certificate.
{{/if}}
{{#if vehicle_state_TX}}
**If {{deceased_first_name}} designated a Transfer on Death beneficiary (Form VTR-121),** the named beneficiary can transfer directly.

**Important:** Deadline: 180 days from the date of passing

**Where to go:** Visit a County Tax Assessor-Collector's office.

**What to do:**
- Confirm the TOD beneficiary designation was filed (Form VTR-121 should be on record with the county).
- Complete Form 130-U (Application for Texas Title and/or Registration).
- Bring everything listed below to the Tax Assessor-Collector's office within 180 days of the date of passing.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| Form 130-U | Application for Texas Title and/or Registration |

[Download Form 130-U](https://www.txdmv.gov/sites/default/files/form_files/130-U.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Completed Form 130-U

**Fees:** Approximately $28 to $33 for the title fee. No motor vehicle sales tax on inheritance transfers. Confirm with your local office before you go.

This must be done within 180 days of the date of passing. The beneficiary must also have survived the owner by at least 120 hours (5 days).

---

You may be able to transfer the vehicle using an Affidavit of Heirship (Form VTR-262) instead of going through full probate. This form needs to be notarized and signed by two witnesses who are not inheriting from the estate.
{{/if}}
{{#if vehicle_state_FL}}
{{#if is_spouse}}
**As the surviving spouse,** you can transfer the vehicle using Form HSMV 82152 - the title transfer fee is waived.

**Where to go:** Visit a County Tax Collector's office.

**What to do:**
- Make sure you have auto insurance in your name before your visit.
- Complete Form HSMV 82040 (Application for Certificate of Title) and Form HSMV 82152 (Surviving Spouse Affidavit).
- Bring everything listed below to the Tax Collector's office.
- Sign as the surviving spouse.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| HSMV 82040 | Application for Certificate of Title |
| HSMV 82152 | Surviving Spouse or Heir Affidavit |

[Download HSMV 82040](https://www.flhsmv.gov/pdf/forms/82040.pdf)
[Download HSMV 82152](https://www.flhsmv.gov/pdf/forms/82152.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title
- Certified copy of the death certificate
- Certified copy of the marriage certificate
- Your photo ID
- Proof of valid Florida auto insurance in your name
- Completed Forms HSMV 82040 and HSMV 82152

**Fees:** The title transfer fee is waived for surviving spouses. Confirm with your local office before you go.

The title transfer fee is waived for surviving spouses. Up to 2 vehicles may be exempt from creditor claims under Florida Statute 732.402.

---

You may be able to skip probate by signing an affidavit confirming the estate can cover its debts and all heirs agree on the transfer. Up to 2 vehicles may be protected from creditor claims.
{{/if}}
{{/if}}
{{#if vehicle_state_IL}}
**If the title includes a Transfer on Death (TOD) beneficiary designation (Form VSD-773),** the named beneficiary can transfer directly.

**Where to go:** Visit a Secretary of State facility.

**What to do:**
- Confirm the TOD beneficiary designation is on file (Form VSD-773).
- Complete Form VSD 190 (Application for Vehicle Transaction) - you can fill this out online through the ERT system at ilsos.gov, then print it.
- Bring everything listed below to a Secretary of State facility.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| VSD 190 | Application for Vehicle Transaction (complete online via ERT system at ilsos.gov) |

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Completed Form VSD 190

**Fees:** Approximately $165 for the title fee. Confirm with your local office before you go.

---

**If the vehicle was jointly owned with survivorship rights,** the surviving owner can transfer with a death certificate (must apply within 120 days).

**Where to go:** Visit a Secretary of State facility.

**What to do:**
- Check the title for survivorship language.
- Complete Form VSD 190.
- Bring everything listed below to a Secretary of State facility within 120 days.
- Sign as the surviving owner.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| VSD 190 | Application for Vehicle Transaction |

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing joint ownership with survivorship)
- Certified copy of the death certificate
- Your photo ID

**Fees:** Approximately $165 for the title fee. Transfers to a surviving spouse are exempt from use tax. Confirm with your local office before you go.

You must apply within 120 days. Transfers to a surviving spouse are fully exempt from use tax.

---

If the estate's personal property is valued under $150,000 (vehicles don't count toward this total), you can use the Small Estate Affidavit (Form RTOPR31) to transfer without full probate. This form needs to be notarized. If the vehicle was jointly owned with survivorship rights, the surviving owner needs to apply within 120 days.
{{/if}}
{{#if vehicle_state_PA}}
**If the vehicle was jointly titled with Right of Survivorship,** the surviving co-owner can transfer with just a death certificate. The title fee and registration transfer fee are both waived.

**Where to go:** Visit a PennDOT Driver and Vehicle Services Center (or by mail).

**What to do:**
- Check the title for Right of Survivorship (ROS) language.
- Complete Form MV-39 (Notification of Assignment/Correction of Vehicle Title Upon Death of Owner).
- Bring everything listed below to PennDOT, or mail in your application.
- Sign as the surviving co-owner. Sections D and F of Form MV-39 must be notarized if completed.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| MV-39 | Notification of Assignment/Correction of Vehicle Title Upon Death of Owner |

[Download MV-39](https://www.pa.gov/content/dam/copapwp-pagov/en/penndot/documents/public/dvspubsforms/bmv/bmv-forms/mv-39.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing Right of Survivorship)
- Certified copy of the death certificate
- Your photo ID
- Completed Form MV-39

**Fees:** Both the title fee and registration transfer fee are waived for Right of Survivorship transfers. Confirm with your local office before you go.

Both the title fee and registration transfer fee are waived for ROS transfers. You can also complete this by mail - include the original title, death certificate, and completed MV-39.

---

If the estate's total value (not counting real estate) is $50,000 or less, you can use a Small Estate Affidavit. If the vehicle was jointly titled with Right of Survivorship, the surviving co-owner can transfer using Form MV-39 with just a death certificate - and both the title fee and registration transfer fee are waived.
{{/if}}
{{#if vehicle_state_OH}}
**If {{deceased_first_name}} set up a Transfer on Death designation (Form BMV 3811),** the named beneficiary can transfer the title directly.

**Where to go:** Visit a County Clerk of Courts Title Office (not a BMV licensing office).

**What to do:**
- Confirm the TOD designation was filed (Form BMV 3811 should be on record).
- Complete Form BMV 3774 (Application for Certificate of Title).
- Bring everything listed below to the Clerk of Courts.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| BMV 3774 | Application for Certificate of Title |

[Download BMV 3774](https://dam.assets.ohio.gov/image/upload/publicsafety.ohio.gov/bmv3774.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Completed Form BMV 3774

**Fees:** Approximately $18 for the title fee, plus county permissive tax (varies by county). Confirm with your local office before you go.

The title fee is approximately $18, plus county permissive tax (varies by county). Call your Clerk of Courts to confirm the exact amount.

---

{{#if is_spouse}}
**As the surviving spouse,** if the total value of all vehicles is $65,000 or less, you can use the simplified affidavit (Form BMV 3773).

**Where to go:** Visit a County Clerk of Courts Title Office.

**What to do:**
- Confirm the total value of all vehicles in the estate is $65,000 or less.
- Complete Form BMV 3773 (Surviving Spouse Affidavit) and Form BMV 3774.
- Bring everything listed below to the Clerk of Courts.
- Sign as the surviving spouse. Form BMV 3773 must be notarized.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| BMV 3773 | Surviving Spouse Affidavit |
| BMV 3774 | Application for Certificate of Title |

[Download BMV 3773](https://dam.assets.ohio.gov/image/upload/publicsafety.ohio.gov/bmv3773.pdf)
[Download BMV 3774](https://dam.assets.ohio.gov/image/upload/publicsafety.ohio.gov/bmv3774.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title
- Certified copy of the death certificate
- Certified copy of the marriage certificate
- Your photo ID
- Completed Forms BMV 3773 (notarized) and BMV 3774

**Fees:** Approximately $18 for the title fee, plus county permissive tax (varies by county). Confirm with your local office before you go.

---

{{/if}}
{{#if is_spouse}}
If the total value of all vehicles in the estate is $65,000 or less, you can use a simplified process (Form BMV 3773) without going through full probate.
{{/if}}
{{/if}}
{{#if vehicle_state_MA}}
**If {{deceased_first_name}} designated a Transfer on Death beneficiary,** the named beneficiary can transfer directly.

**Important:** Deadline: 60 days from the date of passing

**Where to go:** Visit an RMV Service Center.

**What to do:**
- Confirm the TOD beneficiary designation is on the title.
- Complete Form RTA (Registration and Title Application).
- Bring everything listed below to an RMV Service Center within 60 days of the date of passing.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| RTA | Registration and Title Application |

[Download RTA](https://www.mass.gov/doc/registration-and-title-application/download)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Completed Form RTA

**Fees:** Approximately $75 for the title fee. Confirm with your local office before you go.

You must complete this within 60 days of the date of passing. If this deadline is missed, the beneficiary loses all rights and the vehicle must go through the estate. The new owner must have the vehicle inspected within 7 days of transfer.

---

{{#if is_spouse}}
**As the surviving spouse,** Massachusetts law presumes you are a joint tenant with right of survivorship - you can transfer without going through the estate.

**Where to go:** Visit an RMV Service Center.

**What to do:**
- Complete Form RTA (Registration and Title Application).
- Bring everything listed below to an RMV Service Center.
- Sign as the surviving spouse.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| RTA | Registration and Title Application |

[Download RTA](https://www.mass.gov/doc/registration-and-title-application/download)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title
- Certified copy of the death certificate
- Certified copy of the marriage certificate
- Your photo ID
- Completed Form RTA

**Fees:** Approximately $75 for the title fee. Confirm with your local office before you go.

Book an appointment online to save time. The new owner must have the vehicle inspected within 7 days of transfer.

---

{{/if}}
If the estate's personal property is valued at $25,000 or less (vehicles don't count), you can use the Voluntary Administration process (Form MPC 170) instead of full probate.
{{#if is_spouse}}
As the surviving spouse, Massachusetts law lets you transfer the vehicle directly - you don't need to go through Voluntary Administration. This is the simplest path.
{{/if}}
{{/if}}
{{#if vehicle_state_WA}}
**If the title includes a Joint Tenancy with Right of Survivorship (JTWROS) designation,** the surviving owner can transfer with a death certificate.

**Where to go:** Visit a Vehicle Licensing Office (VLO).

**What to do:**
- Check the title for Joint Tenancy with Right of Survivorship (JTWROS) language.
- Complete Form TD-420-001 (Vehicle Certificate of Ownership/Title Application).
- Bring everything listed below to a VLO.
- Sign as the surviving owner. No notarization is required if you complete this in person.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| TD-420-001 | Vehicle Certificate of Ownership/Title Application |

[Download TD-420-001](https://dol.wa.gov/forms/view/420001/download?inline=)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing JTWROS)
- Certified copy of the death certificate
- Your photo ID
- Completed Form TD-420-001

**Fees:** Approximately $6.50 for the filing fee, plus other applicable fees. Confirm with your local office before you go.

---

{{#if is_spouse}}
**As a community property state,** vehicles acquired during the marriage follow a simpler transfer process for the surviving spouse.

**Where to go:** Visit a Vehicle Licensing Office (VLO).

**What to do:**
- Complete Form TD-420-001 and Form TD-420-041 (Affidavit of Inheritance).
- Bring everything listed below to a VLO.
- Sign as the surviving spouse.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| TD-420-001 | Vehicle Certificate of Ownership/Title Application |
| TD-420-041 | Affidavit of Inheritance |

[Download TD-420-001](https://dol.wa.gov/forms/view/420001/download?inline=)
[Download TD-420-041](https://dol.wa.gov/forms/view/420041/download?inline=)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title
- Community Property Agreement (if available)
- Certified copy of the death certificate
- Certified copy of the marriage certificate
- Your photo ID

**Fees:** Approximately $6.50 for the filing fee, plus other applicable fees. Confirm with your local office before you go.

No notarization required if completed in person at a DOL office.

---

{{/if}}
Washington's probate threshold is $100,000, so if the estate is below this amount, you may qualify for a simpler process. If the title includes Joint Tenancy with Right of Survivorship (JTWROS), the surviving owner can transfer with just a death certificate.
{{#if is_spouse}}
If a Community Property Agreement was in place, you can transfer without probate - bring the agreement with you.
{{/if}}
{{/if}}
{{#if vehicle_state_VA}}
**If {{deceased_first_name}} designated a Transfer on Death beneficiary on the title,** the named beneficiary can transfer directly.

**Important:** Deadline: 120 days from the date of passing

**Where to go:** Visit a Virginia DMV Customer Service Center.

**What to do:**
- Confirm the TOD beneficiary designation is on the title.
- Get a Virginia safety inspection at any certified inspection station before your DMV visit.
- Complete Form VSA 66 (Application for Certificate of Title and Registration).
- Bring everything listed below to the DMV within 120 days of the date of passing.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| VSA 66 | Application for Certificate of Title and Registration |

[Download VSA 66](https://www.dmv.virginia.gov/sites/default/files/forms/vsa66.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Virginia safety inspection certificate
- Completed Form VSA 66

**Fees:** Approximately $15 for the title fee, plus local registration fees. Confirm with your local office before you go.

You must complete this within 120 days of the date of passing or the beneficiary loses all rights. A TOD designation is void if there is an active lien (loan) on the vehicle - the lien must be released first.

---

If the estate is valued at $50,000 or less, you can use a Small Estate Affidavit (Form VSA 24) instead of going through probate. This form needs to be notarized. One thing to note: this option isn't available if probate has already been started.
{{/if}}
{{#if vehicle_state_MI}}
**If the vehicle was jointly owned and the title includes "Full Rights to Survivor,"** the surviving owner can transfer with a death certificate.

**Where to go:** Visit a Secretary of State branch office.

**What to do:**
- Check the title for the phrase "Full Rights to Survivor." Without this exact phrase, the vehicle goes through the estate.
- Complete Form TR-11L (Application for Michigan Vehicle Title).
- Bring everything listed below to a Secretary of State branch office.
- Sign as the surviving owner.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| TR-11L | Application for Michigan Vehicle Title |

[Download TR-11L](https://www.michigan.gov/-/media/Project/Websites/sos/27lawensn/tr11L.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing "Full Rights to Survivor")
- Certified copy of the death certificate
- Your photo ID
- Completed Form TR-11L

**Fees:** Approximately $15 for the title fee, plus plate transfer fee (typically $10 to $15). Confirm with your local office before you go.

Book an appointment online - walk-in wait times can be quite long. Write "Inheritance" on Form TR-11L and initial the tax exemption box.

---

If {{deceased_first_name}}'s only probate assets were vehicles totaling $60,000 or less, you can use Form TR-29 (Certification from the Heir to a Vehicle) to transfer without going through probate court.
{{/if}}
{{#if vehicle_state_CO}}
**If {{deceased_first_name}} designated a Transfer on Death beneficiary (Form DR 2009),** the named beneficiary can transfer directly.

**Where to go:** Visit a County Motor Vehicle (County Clerk) office.

**What to do:**
- Confirm the TOD beneficiary designation was filed (Form DR 2009 should be on record).
- Complete Form DR 2395 (Application for Title and/or Registration).
- Bring everything listed below to the County Clerk's office.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| DR 2395 | Application for Title and/or Registration |

[Download DR 2395](https://dmv.colorado.gov/sites/dmv/files/DR2395.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Completed Form DR 2395

**Fees:** Approximately $7.20 for the title fee, plus county-specific fees (total typically $13 to $20). Confirm with your local office before you go.

Requirements can vary slightly by county, so call ahead to confirm. If registering in the Denver metro area, you may need an emissions inspection.

---

If probate isn't required, you can use a Small Estate Affidavit (Form DR 2712) to transfer the vehicle. Colorado's small estate threshold is approximately $86,000 (adjusted each year for inflation).
{{/if}}
{{#if vehicle_state_MD}}
{{#if is_spouse}}
**As the surviving spouse,** if the vehicle was jointly titled, you can transfer with a death certificate. The title fee is waived entirely.

**Where to go:** Visit an MVA (Motor Vehicle Administration) office.

**What to do:**
- You do not need a safety inspection for spouse-to-spouse transfers.
- Complete Form VR-005 (Application for Certificate of Title) and Form VR-151 (You've Inherited a Vehicle).
- Bring everything listed below to an MVA office.
- Sign as the surviving spouse.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| VR-005 | Application for Certificate of Title |
| VR-151 | You've Inherited a Vehicle |

[Download VR-005](https://mva.maryland.gov/Documents/VR-005.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing joint ownership)
- Certified copy of the death certificate
- Certified copy of the marriage certificate
- Your photo ID
- Completed Forms VR-005 and VR-151

**Fees:** The $200 title fee is waived for surviving spouses. Confirm with your local office before you go.

The $200 title fee is waived for spouse-to-spouse transfers, and you are exempt from the safety inspection and excise tax. Make an online appointment - MVA offices get very busy.

---

You'll use Form VR-151 ("You've Inherited a Vehicle") for the transfer. The form name says it all - it's designed specifically for this situation.
{{/if}}
{{/if}}
{{#if vehicle_state_CT}}
**If {{deceased_first_name}} designated a Transfer on Death beneficiary,** the named beneficiary can transfer directly.

**Important:** Deadline: 60 days from the date of passing

**Where to go:** Visit a CT DMV hub office.

**What to do:**
- Confirm the TOD beneficiary designation is on the title.
- Complete Form H-13B (Application for Registration and Title).
- Bring everything listed below to a CT DMV hub office within 60 days of the date of passing.
- Sign as the beneficiary.

**Forms you'll need:**

| Form | Purpose |
| --- | --- |
| H-13B | Application for Registration and Title |

[Download H-13B](https://portal.ct.gov/-/media/dmv/20/29/h13bpdf.pdf)

Need help filling out these forms? Contact us - we'll walk you through each field step by step.

**What to bring:**
- Original vehicle title (showing TOD designation)
- Certified copy of the death certificate
- Your photo ID
- Most recent vehicle registration certificate
- Completed Form H-13B

**Fees:** Approximately $25 for the title fee. Confirm with your local office before you go.

You must complete this within 60 days of the date of passing, or all rights are lost. Check the CT DMV website for current appointment requirements.

---

If the estate is valued at $40,000 or less, you may be able to use a simpler process. A waiting period may apply - check with your local CT DMV or probate court to confirm.
{{/if}}
{{#if vehicle_state_FL}}
{{#if is_spouse}}{{else}}
In {{vehicle_state}}, the no-probate transfer options available are specific to surviving spouses. For other roles, you will need legal authority (such as Letters of Testamentary or Letters of Administration) to complete the transfer. Once you have yours, Elayne will generate your complete, personalized transfer guide.

Have questions about getting your legal authority? Contact us - we can walk you through the process.
{{/if}}
{{/if}}
{{#if vehicle_state_MD}}
{{#if is_spouse}}{{else}}
In {{vehicle_state}}, the no-probate transfer options available are specific to surviving spouses. For other roles, you will need legal authority (such as Letters of Testamentary or Letters of Administration) to complete the transfer. Once you have yours, Elayne will generate your complete, personalized transfer guide.

Have questions about getting your legal authority? Contact us - we can walk you through the process.
{{/if}}
{{/if}}
{{#if vehicle_state_NY}}
**You may qualify for a simpler path to legal authority**

Depending on the estate's value and your relationship, there may be a faster way to get the legal authority you need - without going through full probate.

If the total estate value (including vehicles) is under $25,000, you can use Form MV-349 to transfer without probate. If you're the surviving spouse or a child under 21, use Form MV-349.1 instead. Both forms need to be notarized.
{{/if}}
{{#if vehicle_state_GA}}
**You may qualify for a simpler path to legal authority**

Depending on the estate's value and your relationship, there may be a faster way to get the legal authority you need - without going through full probate.

If probate isn't required, you can use the Affidavit of Inheritance (Form T-20) to transfer the vehicle. This form needs to be notarized.
{{/if}}
{{#if vehicle_state_NC}}
**You may qualify for a simpler path to legal authority**

Depending on the estate's value and your relationship, there may be a faster way to get the legal authority you need - without going through full probate.

If individual vehicles are valued at $5,000 or less each, you can use Form MVR-317 for a simplified transfer. The broader small estate threshold is $20,000. You'll need to get Form MVR-317 certified by the Clerk of Superior Court in the county where {{deceased_first_name}} lived - this is a separate step before visiting the license plate agency.
{{/if}}
{{#if vehicle_state_NJ}}
**You may qualify for a simpler path to legal authority**

Depending on the estate's value and your relationship, there may be a faster way to get the legal authority you need - without going through full probate.

If the estate is $20,000 or less, you can use a simplified transfer process. You'll need a raised seal from the County Surrogate's office (a regular flat seal or photocopy won't be accepted by the MVC).
{{/if}}

# After Your Visit

If you used one of the paths above, here's what to expect once you've submitted everything:

- You should receive a receipt or temporary registration at the office.
- Your new title will typically arrive by mail within 4 to 8 weeks.
- Keep copies of everything you submitted, just in case.
- Update your auto insurance to reflect the new ownership.
- If the vehicle has a registration renewal coming up, check with the office about timing.

# What Happens Next

Once you have your legal authority - whether that's Letters of Testamentary, Letters of Administration, a Small Estate Affidavit, or a Trust Certificate - Elayne will generate your complete transfer guide, tailored to {{vehicle_state}}.

Your guide will include:
- The exact forms you need for {{vehicle_state}}
- A step-by-step walkthrough for filling them out
- Where to go and what to bring
- Fees to expect

No guesswork, no surprises. We're here to make this as smooth as possible. You're already making progress.
{{else}}
# What This Guide Covers

{{requestor_first_name}}, this guide walks you through transferring {{deceased_first_name}}'s vehicle into {{user_full_name}}'s name in {{vehicle_state}}.

We've broken it down into clear steps so you know exactly what to expect.

You don't need to do this all at once. Take it at your own pace.

If anything in this guide is unclear or you'd like personalized help for {{vehicle_state}}, the Elayne care team is here for you. Reach out anytime at careteam@elayne.com or through your Elayne account.

# You'll Need Legal Authority First

Before you can transfer a vehicle title out of your loved one's name, the state needs to know you're authorized to act on behalf of the estate. This comes in the form of a legal document, and you may not have it yet. That's completely normal.

Here are the most common types of legal authority:
- **Letters Testamentary (LOT)** - issued by a probate court when there's a will
- **Letters of Administration** - issued by a probate court when there's no will
- **Small Estate Affidavit** - a simplified option for estates under a certain value (thresholds typically range from $20,000 to $200,000 depending on the state)
- **Certification of Trust** - if assets were held in a trust

In most cases, you'll need one of these documents before the title office can process the transfer. (Some exceptions exist - for example, surviving spouses with jointly titled vehicles or Transfer on Death beneficiaries may have a simpler path. See the Exemptions section below.)

Either way, that doesn't mean you have to wait to get started - there are meaningful things you can do right now.

# What You Can Do Right Now

While you're waiting for your legal authority, here are some steps you can take to stay ahead. Every little bit helps.

- [ ] Keep the vehicle in a safe, secure location.
- [ ] Maintain insurance coverage. Most policies stay active for a period after a loss, but check with your provider to be sure.
- [ ] Gather the documents you'll need later: a certified copy of the death certificate, the vehicle's current title (check the glove box, filing cabinet, or safe), and your photo ID.
- [ ] Hold off on signing the back of the title for now. Wait until you have your legal documents in hand.

# Exemptions That May Apply

Before going through the full transfer process, it's worth checking whether you qualify for a simpler path. Many states offer options that could save you time, paperwork, or money.

Not sure which path applies to you? Check the physical title document for terms like "Transfer on Death," "TOD," "Joint Tenancy," "Right of Survivorship," or "or" between owner names. If you can't find the title, contact us - we can help you figure out your options.

- **Small estate affidavits** - if the total estate value is below your state's threshold (typically between $20,000 and $200,000, depending on the state), you may be able to transfer the vehicle without going through full probate.
- **Transfer on Death (TOD) designations** - if your loved one registered a TOD beneficiary on the title, the named beneficiary may be able to transfer the vehicle directly, without probate.
- **Surviving spouse transfers** - many states offer simplified or fee-waived transfers for surviving spouses.
- **Tax exemptions** - inheritance transfers are often exempt from sales or use tax, though this varies by state.

We recommend contacting your local title office in {{vehicle_state}} to ask about any exemptions that may apply to your situation. You can also reach out to us through your Elayne account - we're happy to help you figure out what options are available.

# What Happens Next

Once you have your legal authority - whether that's Letters Testamentary, Letters of Administration, a Small Estate Affidavit, or a Certification of Trust - or if you qualify as a surviving spouse or TOD beneficiary, Elayne will generate your complete transfer guide with the details specific to {{vehicle_state}}.

No guesswork, no surprises. We're here to make this as smooth as possible. You're already making progress.

Have questions while you wait? We're here to help. Reach out to the Elayne care team at careteam@elayne.com or through your Elayne account - no question is too small.
{{/if}}

---

*This guide is provided for informational purposes only and does not constitute legal advice. Requirements may vary based on your specific situation. We recommend consulting with a legal professional or contacting your local title office to confirm current requirements. Elayne is not responsible for any errors or changes in state requirements.*`;
